// The floor plan editor's state — a pure reducer, so every action and the undo stack are
// testable without a DOM. The view (FloorplanView.jsx) turns pointer gestures into these
// actions and, on Done, sends `diff()` to PUT /floorplan/layout in one request.
//
// Geometry mirrors backend/floorplan.py: percent of the plan, a 2% grid, features never
// smaller than 6% a side (a wall may be 1% thin one way), tables never smaller than 4%.
// The server re-applies the same rules; these exist so what you see while dragging is
// what gets saved.

export const GRID = 2;
export const MIN_SIZE = 6;
export const WALL_MIN = 1;
export const TABLE_MIN = 4;

export const snap = (v) => Math.floor(v / GRID + 0.5) * GRID;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const normaliseFeature = (f) => {
  const kind = f.kind || 'label';
  let minW = MIN_SIZE;
  let minH = MIN_SIZE;
  if (kind === 'wall') {
    if ((f.w || 0) >= (f.h || 0)) minH = WALL_MIN; else minW = WALL_MIN;
  }
  let w = clamp(snap(f.w ?? MIN_SIZE), minW, 100);
  let h = clamp(snap(f.h ?? MIN_SIZE), minH, 100);
  if (kind === 'wall' && minH === WALL_MIN) h = Math.max(WALL_MIN, Math.round(f.h ?? WALL_MIN));
  if (kind === 'wall' && minW === WALL_MIN) w = Math.max(WALL_MIN, Math.round(f.w ?? WALL_MIN));
  const x = clamp(snap(f.x ?? 0), 0, 100 - w);
  const y = clamp(snap(f.y ?? 0), 0, 100 - h);
  return { ...f, kind, x, y, w, h, rotation: f.rotation === 90 ? 90 : 0 };
};

// A table's box: centre (`position_x/y`) plus a size in percent when it has one. `width` /
// `height` of 0/1 mean "auto from capacity" — the view decides the pixels for those.
export const normaliseTable = (t) => {
  const out = { ...t };
  // (0, 0) is "not on the plan yet" (the unplaced chips); anything else stays inside it.
  const unplaced = !(t.position_x > 0 || t.position_y > 0);
  out.position_x = unplaced ? 0 : clamp(Math.round(t.position_x), 1, 99);
  out.position_y = unplaced ? 0 : clamp(Math.round(t.position_y), 1, 99);
  for (const k of ['width', 'height']) {
    const v = Math.round(t[k] ?? 1);
    out[k] = v < TABLE_MIN ? 1 : Math.min(60, v);
  }
  return out;
};

export const DEFAULT_LABEL = {
  kitchen: 'Kitchen', bar: 'Bar', entrance: 'Entrance', host_stand: 'Host stand',
  toilets: 'WC', wall: '', till: 'Till', label: 'Label',
};
const DEFAULT_SIZE = {
  kitchen: [20, 20], bar: [24, 12], entrance: [12, 8], host_stand: [8, 6],
  toilets: [12, 12], wall: [30, 1], till: [8, 6], label: [14, 6],
};

let seq = 0;
const tempId = () => `new-${Date.now().toString(36)}-${(seq += 1)}`;

// ---------- state ----------

export const start = (tables, features) => ({
  tables: tables.map(normaliseTable),
  features: features.map(normaliseFeature),
  deleted: [],
  selected: null,
  past: [],
  future: [],
});

const snapshot = (s) => ({ tables: s.tables, features: s.features, deleted: s.deleted });
const commit = (s, next) => ({
  ...s, ...next,
  past: [...s.past.slice(-49), snapshot(s)],
  future: [],
});

const updateFeature = (s, id, patch) => ({
  features: s.features.map((f) => (f.id === id ? normaliseFeature({ ...f, ...patch }) : f)),
});
const updateTable = (s, id, patch) => ({
  tables: s.tables.map((t) => (t.id === id ? normaliseTable({ ...t, ...patch }) : t)),
});

export function reduce(s, action) {
  switch (action.type) {
    case 'reset':
      return action.state;
    // Tables that arrived from the server mid-edit (one just created from the Add Table
    // dialog): join the draft as they are, no undo step — they aren't an edit.
    case 'absorbTables': {
      const known = new Set(s.tables.map((t) => t.id));
      const fresh = action.tables.filter((t) => !known.has(t.id)).map(normaliseTable);
      return fresh.length ? { ...s, tables: [...s.tables, ...fresh] } : s;
    }
    case 'select':
      return { ...s, selected: action.sel };

    // A move or resize in progress: no undo step yet, so a drag is one step, not fifty.
    case 'preview': {
      const { sel, patch } = action;
      return sel.type === 'feature'
        ? { ...s, ...updateFeature(s, sel.id, patch) }
        : { ...s, ...updateTable(s, sel.id, patch) };
    }
    // The gesture ended: record the step against the state it started from.
    case 'commit':
      return { ...s, past: [...s.past.slice(-49), action.from], future: [] };

    case 'rename':
      return commit(s, updateFeature(s, action.id, { label: String(action.label ?? '').slice(0, 40) }));
    case 'setKind':
      return commit(s, updateFeature(s, action.id, {
        kind: action.kind,
        label: s.features.find((f) => f.id === action.id)?.label || DEFAULT_LABEL[action.kind],
      }));
    case 'rotate': {
      const f = s.features.find((x) => x.id === action.id);
      if (!f) return s;
      // Turning 90° swaps the box's sides about its centre, kept inside the plan.
      const cx = f.x + f.w / 2;
      const cy = f.y + f.h / 2;
      return commit(s, updateFeature(s, action.id, {
        rotation: f.rotation === 90 ? 0 : 90, w: f.h, h: f.w, x: cx - f.h / 2, y: cy - f.w / 2,
      }));
    }
    case 'renameTable':
      return commit(s, updateTable(s, action.id, { name: String(action.name ?? '').trim().slice(0, 20) || s.tables.find((t) => t.id === action.id)?.name }));
    case 'setTableShape':
      return commit(s, updateTable(s, action.id, { shape: action.shape }));
    // Which area of the room (Main, Terrace…) a table or feature belongs to.
    case 'setSection': {
      const section = String(action.section || 'Main').slice(0, 40);
      return action.sel.type === 'feature'
        ? commit(s, updateFeature(s, action.sel.id, { section }))
        : commit(s, updateTable(s, action.sel.id, { section }));
    }

    case 'addFeature': {
      const [w, h] = DEFAULT_SIZE[action.kind] || DEFAULT_SIZE.label;
      const f = normaliseFeature({
        id: tempId(), kind: action.kind, label: DEFAULT_LABEL[action.kind] ?? '',
        x: 50 - w / 2, y: 50 - h / 2, w, h, rotation: 0, z: 0, section: action.section || 'Main',
      });
      return commit(s, { features: [...s.features, f], selected: { type: 'feature', id: f.id } });
    }
    case 'placeTable':
      // An unplaced table (0,0) dropped into the middle of the plan, ready to be dragged.
      return commit(s, {
        ...updateTable(s, action.id, { position_x: 50, position_y: 50, ...(action.section ? { section: action.section } : {}) }),
        selected: { type: 'table', id: action.id },
      });

    // The back office's table list (Oct 2026: set-up moved there). A new table gets a
    // temporary id; the server gives it a real one on Save.
    case 'addTable': {
      const t = normaliseTable({
        id: action.id || tempId(), name: String(action.name || '').trim().slice(0, 20), capacity: Number(action.capacity) || 2,
        section: action.section || 'Main', shape: action.shape || 'square',
        position_x: action.position_x ?? 50, position_y: action.position_y ?? 50, width: 1, height: 1,
      });
      return commit(s, { tables: [...s.tables, t], selected: { type: 'table', id: t.id } });
    }
    case 'updateTable':
      return commit(s, updateTable(s, action.id, action.patch || {}));
    case 'deleteTable':
      return commit(s, { tables: s.tables.filter((t) => t.id !== action.id), selected: null });
    // An area removed: what was in it moves to another, in one undo step.
    case 'moveSection': {
      const gone = (x) => (x.section || 'Main') === action.from;
      return commit(s, {
        tables: s.tables.map((t) => (gone(t) ? { ...t, section: action.to } : t)),
        features: s.features.map((f) => (gone(f) ? { ...f, section: action.to } : f)),
        selected: null,
      });
    }

    case 'deleteFeature': {
      const isNew = String(action.id).startsWith('new-');
      return commit(s, {
        features: s.features.filter((f) => f.id !== action.id),
        deleted: isNew ? s.deleted : [...s.deleted, action.id],
        selected: null,
      });
    }

    case 'undo': {
      if (!s.past.length) return s;
      const prev = s.past[s.past.length - 1];
      return { ...s, ...prev, selected: null, past: s.past.slice(0, -1), future: [snapshot(s), ...s.future] };
    }
    case 'redo': {
      if (!s.future.length) return s;
      const [next, ...rest] = s.future;
      return { ...s, ...next, selected: null, past: [...s.past, snapshot(s)], future: rest };
    }
    default:
      return s;
  }
}

// ---------- the save ----------

const GEOM_T = ['position_x', 'position_y', 'width', 'height', 'shape', 'section', 'name'];
const GEOM_F = ['kind', 'label', 'x', 'y', 'w', 'h', 'rotation', 'z', 'section'];
const changed = (a, b, keys) => keys.some((k) => (a?.[k] ?? null) !== (b?.[k] ?? null));

// What to send: new features (no id), changed features, deleted ids, changed table boxes.
export function diff(state, original) {
  const origF = new Map(original.features.map((f) => [f.id, normaliseFeature(f)]));
  const origT = new Map(original.tables.map((t) => [t.id, normaliseTable(t)]));
  const features = [];
  for (const f of state.features) {
    if (String(f.id).startsWith('new-')) {
      const { id, ...rest } = f;
      features.push(rest);
    } else if (changed(origF.get(f.id), f, GEOM_F)) {
      features.push(f);
    }
  }
  const tables = state.tables
    .filter((t) => changed(origT.get(t.id), t, GEOM_T))
    .map((t) => ({ id: t.id, position_x: t.position_x, position_y: t.position_y, width: t.width, height: t.height, shape: t.shape, section: t.section, name: t.name }));
  return { features, deleted: state.deleted.filter((id) => origF.has(id)), tables };
}

export const isDirty = (state, original) => {
  const d = diff(state, original);
  return d.features.length > 0 || d.deleted.length > 0 || d.tables.length > 0;
};

/** The plan as the back office saves it: temporary ids dropped, so the server gives new
 *  tables and fixtures real ones. */
export function planFrom(state, areas) {
  const strip = (row) => (String(row.id).startsWith('new-') ? (({ id, ...rest }) => rest)(row) : row);
  return {
    tables: state.tables.map((t) => strip({
      id: t.id, name: t.name, capacity: t.capacity, section: t.section || 'Main', shape: t.shape || 'square',
      position_x: t.position_x, position_y: t.position_y, width: t.width, height: t.height,
    })),
    features: state.features.map((f) => strip({
      id: f.id, kind: f.kind, label: f.label, x: f.x, y: f.y, w: f.w, h: f.h, rotation: f.rotation || 0, z: f.z || 0,
      section: f.section || 'Main',
    })),
    areas: [...areas],
  };
}
